/**
 * The purpose of this file is to gather all the get/put/post/delete calls that
 * are needed in the kelda client code in a way that hides the details such as
 * client configuration and path templates. However, gathering all functions in
 * one place tends to create modules with heavy transitive imports. Therefore
 * this module might need to be further split into smaller modules in the
 * future.
 */
import { globalBackendClient } from '@mimir/client-common/globals/backendClient';
import type { KeldaRequestCreationDto, KeldaRequestGetResponseDto, KeldaRequestUpdateDto, KeldaRequestUpdateResponseDto } from '@mimir/common/dto/keldaRequestDtos';
import type { HttpQueryParameters } from '@mimir/common/util/apiClient';
import type { CreateKeldaConfigDto, KeldaConfigDto, UpdateKeldaConfigDto } from '@mimir/common/dto/keldaConfigDto';
import type { KeldaStatusDto, UpdateOrCreateKeldaStatusDto } from '@mimir/common/dto/statusDto';
import type { PresignedURLMethodType } from '@mimir/common/dto/keldaS3ServerKeysTypes';
import type { KeldaAdminStatusDto } from '@mimir/common/dto/keldaAdminDto';
import { api_createKeldaRequest, api_deleteKeldaRequest, api_deleteKeldaRequests, api_getAllKeldaStatuses, api_getKeldaRequest, api_getKeldaRequests, api_getKeldaS3ServerKeysPresignedURL, api_getKeldaStatuses, api_getKeldaStatusesForConfiguration, api_updateKeldaConfig, api_updateKeldaRequest, api_updateOrCreateKeldaStatus } from '@mimir/client-common/api/keldaApi';
import { CreateKeldaCommand, DeleteKeldaCommand, GetKeldaCommand, ListKeldasCommand } from '@mimir/common/api/keldaCommands';
import { callApiCommand } from '@mimir/client-common/api/callApiCommand';


export const getKeldaRequest = async (processorRequestId: string) => api_getKeldaRequest(globalBackendClient, processorRequestId);

export const getKeldaRequests = async (supportTenantId?: string) => api_getKeldaRequests(globalBackendClient, supportTenantId);

export const deleteKeldaRequests = async (placeholderId: string, supportTenantId?: string) => api_deleteKeldaRequests(globalBackendClient, placeholderId, supportTenantId);

export const deleteKeldaRequest = async (keldaRequestId: string, supportTenantId?: string) => api_deleteKeldaRequest(globalBackendClient, keldaRequestId, supportTenantId);

export const getAllKeldaStatuses = (): Promise<KeldaAdminStatusDto[]> =>
  api_getAllKeldaStatuses(globalBackendClient);

/************************ Kelda ************************/

export const createKeldaRequest = async (body: KeldaRequestCreationDto, supportTenantId?: string): Promise<KeldaRequestGetResponseDto> =>
  api_createKeldaRequest(globalBackendClient, body, supportTenantId !== undefined ? { queryParams: { supportTenantId }} : {});

export const updateKeldaRequest = async (requestId: string, body: KeldaRequestUpdateDto): Promise<KeldaRequestUpdateResponseDto> =>
  api_updateKeldaRequest(globalBackendClient, requestId, body);

export async function createKeldaConfig(body: CreateKeldaConfigDto): Promise<KeldaConfigDto> {
  return callApiCommand(globalBackendClient, new CreateKeldaCommand({ config: body }), undefined);
}

export async function getKeldaConfigs(queryParams: HttpQueryParameters = {}): Promise<KeldaConfigDto[]> {
  return callApiCommand(globalBackendClient, new ListKeldasCommand({}), { queryParams });
}

export async function getKeldaConfig(configId: string): Promise<KeldaConfigDto> {
  return callApiCommand(globalBackendClient, new GetKeldaCommand({ configId }), undefined);
}

export async function deleteKeldaConfig(configId: string): Promise<void> {
  return callApiCommand(globalBackendClient, new DeleteKeldaCommand({ configId }), undefined);
}

export const updateKeldaConfig = async (updateConfigDto: UpdateKeldaConfigDto) =>
  api_updateKeldaConfig(globalBackendClient, updateConfigDto);

export const getKeldaStatuses = async (): Promise<KeldaStatusDto[]> =>
  api_getKeldaStatuses(globalBackendClient);

export const getKeldaStatusForConfiguration = async (configId: string): Promise<KeldaStatusDto[]> =>
  api_getKeldaStatusesForConfiguration(globalBackendClient, configId);

export const updateOrCreateKeldaStatus = async (configId: string, updateOrCreateDto: UpdateOrCreateKeldaStatusDto): Promise<KeldaStatusDto> =>
  api_updateOrCreateKeldaStatus(globalBackendClient, configId, updateOrCreateDto);

export const getKeldaS3ServerKeysPresignedURL = (keldaId: string, method: PresignedURLMethodType) => {
  return api_getKeldaS3ServerKeysPresignedURL(globalBackendClient, keldaId, { queryParams: { method }});
};

